import React, { useEffect } from "react";
import { ResponsiveHeatMap } from "@nivo/heatmap";
import { Box, Checkbox, Flex, Text } from "@chakra-ui/react";
import { getGapAnalysisDashboardData, getQuestionGroupList, STAGETYPE_LABEL } from "../../dashboardDataUtils";
import { useSearchParams } from "react-router-dom";
import SearchString from "search-string";
import { STATUSFIELD_VALUE_LABEL } from "../../../IssbDataInput/components/AdditionalStatuses";
import _ from "lodash";
import { gotoFilteredQuestionList } from "../AIDashboard/AIDashboard";
import { useNavigate } from "react-router-dom";
import { nivo_theme } from "../chart-utils";

const GapAnalysis = ({ questions }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = React.useState([]);
  const [groupFilter, setGroupFilter] = React.useState([]);

  const questionGroups = getQuestionGroupList(questions);

  // Bleurgh: map condensed-stage-type to stageNames
  const stageNamesForStageTypeCondensed = {};
  questions.forEach((question) => {
    const stageTypeCondensed = STAGETYPE_LABEL[question.stageType];
    let stageNames = stageNamesForStageTypeCondensed[stageTypeCondensed];
    if (!stageNames) {
      stageNames = [];
      stageNamesForStageTypeCondensed[stageTypeCondensed] = stageNames;
    }
    if (!stageNames.includes(question.stage)) {
      stageNames.push(question.stage);
    }
  });

  const handleClick = (stageType, confidence) => {
    const searchString = SearchString.parse("");
    const stageNames = stageNamesForStageTypeCondensed[stageType];
    const stageFilter = stageNames.join(","); // comma-separated list
    searchString.addEntry("stage", stageFilter);
    searchString.addEntry("confidence", confidence);
    // if groupfilter is not equal to all groups, add group filter to search string
    if (groupFilter.length !== questionGroups.length) {
      groupFilter.map((group) => searchString.addEntry("questionGroup", group));
    }
    // TODO support all / my Qs filter
    const newParams = new URLSearchParams(searchParams);
    newParams.set("q", searchString.toString());
    gotoFilteredQuestionList(navigate, null, null, newParams);
  };

  const handleGroupFilter = (group) => {
    if (groupFilter.includes(group)) {
      setGroupFilter(groupFilter.filter((g) => g !== group));
    } else {
      setGroupFilter([...groupFilter, group]);
    }
  };

  useEffect(() => {
    setGroupFilter(getQuestionGroupList(questions));
  }, [questions]);

  useEffect(() => {
    setData(getGapAnalysisDashboardData(questions, groupFilter));
  }, [questions, groupFilter]);

  const CustomTooltip = ({ stageType, confidence, value }) => (
    <Box borderRadius="md" bg="pureWhite">
      <Text p={2} color="darkTeal">
        Stage: {STAGETYPE_LABEL[stageType] || stageType}
      </Text>
      <Text p={2} color="darkTeal">
        Confidence: {STATUSFIELD_VALUE_LABEL.confidence[confidence]}
      </Text>
      <Text p={2} color="darkTeal">
        Count: {value}
      </Text>
    </Box>
  );

  return (
    <Flex direction="column" borderRadius="lg" border="solid 1px" borderColor="gray.200" p={4} gap={4}>
      <Text color="gray.700" fontSize="heading.xs" fontWeight="bold">
        Gap Analysis
      </Text>

      <Box height="500px">
        {/* Add Group Filter checkbox */}
        <Flex direction="row" gap={4}>
          {questionGroups.map((group) => (
            <Checkbox
              key={group}
              onChange={() => handleGroupFilter(group)}
              isChecked={groupFilter.includes(group)}
              colorScheme="limeShade"
              color="teal"
            >
              <Text color="teal" fontSize="body.s">
                {group}
              </Text>
            </Checkbox>
          ))}
        </Flex>
        <ResponsiveHeatMap
          data={data}
          margin={{ top: 60, right: 70, bottom: 60, left: 120 }}
          valueFormat=".0f"
          axisTop={{
            tickSize: 5,
            tickPadding: 5,
            // tickRotation: -90,
            legend: "",
            legendPosition: "middle",
            legendOffset: -50,
            truncateTickAt: 0,
            format: (d) => STATUSFIELD_VALUE_LABEL.confidence[d] ?? d,
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: "",
            legendPosition: "middle",
            legendOffset: -70,
            truncateTickAt: 0,
            format: (d) => STAGETYPE_LABEL[d] ?? d,
          }}
          axisRight={{
            tickSize: 0,
            tickPadding: 0,
            legend: "Complete",
            legendPosition: "middle",
            legendOffset: 20,
            tickValues: [],
          }}
          axisBottom={{
            tickSize: 0,
            tickPadding: 0,
            legend: "Confidence",
            legendPosition: "middle",
            legendOffset: 20,
            tickValues: [],
          }}
          colors={(d) => d.data.color}
          labelTextColor={(d) => d.data.textColor}
          onClick={(data) => handleClick(data?.serieId, data?.data?.x)}
          tooltip={(data) => {
            return (
              <CustomTooltip
                stageType={data?.cell?.serieId}
                confidence={data?.cell?.data?.x}
                value={data?.cell?.value}
              />
            );
          }}
          emptyColor="#555555"
          theme={nivo_theme}
        />
      </Box>
    </Flex>
  );
};

export default GapAnalysis;
